import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { updateCollection } from '@platform/client';
import { courseColourSchema } from '@platform/schema/content';
import { courseRamp, readableHue, v2 } from '@platform/tokens';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useTrackedMutation } from '../actions/ActionsProvider';

import { collectionChanged } from './collection';

import type { Collection } from './collection';
import type { CoursePalette } from '@platform/schema/content';

type Scheme = keyof typeof v2.color;

const isHex = (value: string) => courseColourSchema.safeParse(value).success;

/** Where an unchosen pair starts: the colours the course wears today. */
const STARTING_POINT: CoursePalette = {
  primary: v2.color.light.subject.growth.hue,
  secondary: v2.color.light.subject.growth.mid,
};

/**
 * A course's two colours — VIS-18.
 *
 * Saved as chosen, even one nothing reads on: Eden keeps the final say, and the app
 * moves only text and interface icons to a shade that reads. The preview shows that
 * shade in both themes before the pair is saved, so a bad result is seen here first.
 */
export function CourseTheme({ row }: { row: Collection }) {
  const queries = useQueryClient();
  const [draft, setDraft] = useState<CoursePalette>(
    row.palette ?? STARTING_POINT,
  );

  const save = useTrackedMutation('Save the course colours', {
    mutationFn: async (palette: CoursePalette | null) => {
      await updateCollection({
        path: { id: row.id },
        body: { palette },
        throwOnError: true,
      });
    },
    onSuccess: () => collectionChanged(queries, row.id),
  });

  const valid = isHex(draft.primary) && isHex(draft.secondary);
  const unchanged =
    row.palette?.primary === draft.primary &&
    row.palette.secondary === draft.secondary;
  const shifted = valid
    ? (['light', 'dark'] as const).filter(
        (scheme) => readableHue(draft.primary, scheme) !== draft.primary,
      )
    : [];

  return (
    <Stack spacing={2} data-testid="course-theme">
      <Typography variant="h6">Visual theme</Typography>
      <Typography variant="body2" color="text.secondary">
        {row.palette
          ? 'This course, its modules and its lessons wear these two colours.'
          : 'No colours chosen: the course wears its topic’s colours until you save a pair.'}
      </Typography>

      <Stack direction="row" spacing={2}>
        <ColourField
          label="Primary colour"
          value={draft.primary}
          onChange={(primary) => {
            setDraft({ ...draft, primary });
          }}
        />
        <ColourField
          label="Secondary colour"
          value={draft.secondary}
          onChange={(secondary) => {
            setDraft({ ...draft, secondary });
          }}
        />
      </Stack>

      {shifted.length > 0 && (
        <Alert severity="warning" data-testid="course-theme-shifted">
          The primary is hard to read as text in {shifted.join(' and ')} mode.
          Text and interface icons there use a readable shade of it, shown
          below; artwork keeps the colour you chose.
        </Alert>
      )}

      {valid && (
        <Stack direction="row" spacing={2}>
          <Preview palette={draft} scheme="light" title={row.title} />
          <Preview palette={draft} scheme="dark" title={row.title} />
        </Stack>
      )}

      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          disabled={!valid || unchanged || save.isPending}
          onClick={() => {
            save.mutate(draft);
          }}
        >
          Save colours
        </Button>
        {row.palette && (
          <Button
            disabled={save.isPending}
            onClick={() => {
              setDraft(STARTING_POINT);
              save.mutate(null);
            }}
          >
            Use the topic’s colours
          </Button>
        )}
      </Stack>

      {row.sharedLessons.length > 0 && (
        <Alert severity="error" data-testid="course-theme-shared">
          These lessons also sit in another course’s module, so which colours
          they wear is not settled. Keep each in one course’s module:
          <ul>
            {row.sharedLessons.map((lesson) => (
              <li key={lesson.id}>
                {lesson.title} — also in {lesson.otherCourses.join(', ')}
              </li>
            ))}
          </ul>
        </Alert>
      )}
    </Stack>
  );
}

/** A picker and the hex it stands for, either editable. The hex is kept lowercase. */
function ColourField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (hex: string) => void;
}) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      <input
        type="color"
        aria-label={`${label} picker`}
        value={isHex(value) ? value : STARTING_POINT.primary}
        onChange={(event) => {
          onChange(event.target.value.toLowerCase());
        }}
      />
      <TextField
        label={label}
        value={value}
        error={!isHex(value)}
        helperText={isHex(value) ? ' ' : 'Six hex digits, like #0f766e'}
        onChange={(event) => {
          onChange(event.target.value.trim().toLowerCase());
        }}
      />
    </Stack>
  );
}

/**
 * A course symbol and a module card in one theme, drawn from the ramp the app derives —
 * the same `courseRamp` — so the shades shown are the shades a learner sees.
 */
function Preview({
  palette,
  scheme,
  title,
}: {
  palette: CoursePalette;
  scheme: Scheme;
  title: string;
}) {
  const ramp = courseRamp(palette, scheme);
  const theme = v2.color[scheme];
  const { size, space, radius } = v2;

  return (
    <Box
      data-testid={`course-theme-preview-${scheme}`}
      sx={{
        flex: 1,
        p: `${String(space.lg)}px`,
        borderRadius: `${String(radius.panel)}px`,
        bgcolor: theme.surface.base,
        color: theme.text.primary,
      }}
    >
      <Typography variant="caption" sx={{ color: theme.text.secondary }}>
        {scheme === 'light' ? 'Light' : 'Dark'}
      </Typography>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mt: 1 }}>
        <Box
          aria-label="Course symbol"
          sx={{
            position: 'relative',
            flexShrink: 0,
            width: `${String(size.label)}px`,
            height: `${String(size.label)}px`,
            borderRadius: `${String(radius.panel)}px`,
            bgcolor: palette.primary,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              right: `${String(space.sm)}px`,
              bottom: `${String(space.sm)}px`,
              width: `${String(size.lead)}px`,
              height: `${String(size.lead)}px`,
              borderRadius: `${String(radius.pill)}px`,
              bgcolor: palette.secondary,
            }}
          />
        </Box>
        <Box
          aria-label="Module card"
          sx={{
            flex: 1,
            p: `${String(space.md)}px`,
            borderRadius: `${String(radius.panel)}px`,
            bgcolor: ramp.surface,
            border: `${String(space.hair)}px solid ${ramp.edge}`,
          }}
        >
          <Typography
            variant="overline"
            data-testid={`course-theme-eyebrow-${scheme}`}
            sx={{ color: ramp.hue, fontWeight: 700 }}
          >
            Module 1 · {ramp.hue}
          </Typography>
          <Typography variant="subtitle2">{title}</Typography>
          <Box
            sx={{
              mt: 1,
              height: `${String(space.sm)}px`,
              borderRadius: `${String(radius.pill)}px`,
              bgcolor: theme.surface.sunken,
            }}
          >
            <Box
              sx={{
                width: '40%',
                height: '100%',
                borderRadius: `${String(radius.pill)}px`,
                bgcolor: ramp.mid,
              }}
            />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}
